import { GitCompare, Users } from 'lucide-react'
import {
  formatToolValue,
  getToolCallId,
  isToolError,
  normalizeTone,
  pickString,
  plural,
  readToolState,
  ToolListPreview,
  ToolStatusLine,
  type ToolPartProps,
  type ToolState,
} from './_base'

const usersIcon = <Users className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />

function buildPopulationDetail(state: ToolState): string | null {
  if (Array.isArray(state.args?.providers) && state.args.providers.length > 0) {
    return state.args.providers.join(', ')
  }
  return null
}

function populationRecords(result: unknown): Array<Record<string, unknown>> {
  const objectResult =
    result && typeof result === 'object'
      ? (result as Record<string, unknown>)
      : {}
  const records =
    [
      objectResult.records,
      objectResult.items,
      objectResult.users,
      objectResult.data,
    ].find(Array.isArray) ?? []
  return records as Array<Record<string, unknown>>
}

function displayName(record: Record<string, unknown>): string {
  return (
    pickString(
      record.name,
      record.displayName,
      record.fullName,
      record.email,
      record.username,
      record.userName,
      record.login,
      record.accountId
    ) ?? 'Unknown identity'
  )
}

function fieldValue(record: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = formatToolValue(record[key])
    if (value) return value
  }
  return null
}

function detailFields(record: Record<string, unknown>) {
  const hidden = new Set([
    'id',
    '_id',
    'internalId',
    'workspaceId',
    'organizationId',
    'raw',
    'metadata',
  ])
  return Object.entries(record)
    .filter(([key, value]) => !hidden.has(key) && formatToolValue(value))
    .slice(0, 24)
    .map(([key, value]) => ({
      label: key,
      value: formatToolValue(value),
      tone: normalizeTone(value),
    }))
}

function PopulationListView({
  part,
  loadingLabel,
  unit,
}: ToolPartProps & { loadingLabel: string; unit: string }) {
  const state = readToolState(part)
  const records = populationRecords(state.result)
  const total =
    typeof state.result?.total === 'number'
      ? state.result.total
      : typeof state.result?.summary?.totalRecords === 'number'
        ? state.result.summary.totalRecords
        : records.length > 0
          ? records.length
          : null
  const completedSources = state.result?.summary?.completedSources
  let completedLabel: string
  if (total !== null) {
    completedLabel = `Loaded ${plural(total, unit)}`
  } else if (typeof completedSources === 'number') {
    completedLabel = `Population data loaded from ${plural(completedSources, 'source')}`
  } else {
    completedLabel = 'Population data loaded'
  }
  const detail = buildPopulationDetail(state)
  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel={loadingLabel}
        completedLabel={completedLabel}
        loadingDetail={detail}
        completedDetail={detail}
        completedIcon={usersIcon}
      />
      {state.isComplete && !isToolError(state) ? (
        <ToolListPreview
          emptyLabel='No population records found'
          items={records.map((record, index) => {
            const title = displayName(record)
            return {
              id: null,
              title,
              subtitle: pickString(
                record.email,
                record.provider,
                record.source,
                record.origin
              ),
              meta: [
                fieldValue(record, 'role', 'permission', 'accessLevel', 'type'),
                fieldValue(record, 'status', 'state', 'mfaStatus'),
                fieldValue(record, 'provider', 'source', 'lastSeenAt'),
              ],
              tone: normalizeTone(fieldValue(record, 'status', 'mfaStatus')),
              detailOverlay: {
                id: getToolCallId(part, `population-${index}-${title}`),
                payload: {
                  entityType: 'identity',
                  toolName: loadingLabel.replace(/\.+$/, ''),
                  title,
                  description: pickString(
                    record.description,
                    record.email,
                    record.provider
                  ),
                  fields: detailFields(record),
                },
              },
            }
          })}
        />
      ) : null}
    </>
  )
}

export function PopulationListUsersToolMessage(props: ToolPartProps) {
  return (
    <PopulationListView {...props} loadingLabel='Loading users...' unit='user' />
  )
}

export function PopulationListPrivilegedUsersToolMessage(props: ToolPartProps) {
  return (
    <PopulationListView
      {...props}
      loadingLabel='Loading privileged users...'
      unit='privileged user'
    />
  )
}

export function PopulationListCodeAccessUsersToolMessage(props: ToolPartProps) {
  return (
    <PopulationListView
      {...props}
      loadingLabel='Loading code-access users...'
      unit='code-access user'
    />
  )
}

export function PopulationListIdentityCenterUsersToolMessage(
  props: ToolPartProps
) {
  return (
    <PopulationListView
      {...props}
      loadingLabel='Loading Identity Center users...'
      unit='Identity Center user'
    />
  )
}

export function PopulationListOrgMembersToolMessage(props: ToolPartProps) {
  return (
    <PopulationListView
      {...props}
      loadingLabel='Loading org members...'
      unit='org member'
    />
  )
}

export function PopulationDiffToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const added =
    typeof state.result?.added === 'number'
      ? state.result.added
      : Array.isArray(state.result?.added)
        ? state.result.added.length
        : null
  const removed =
    typeof state.result?.removed === 'number'
      ? state.result.removed
      : Array.isArray(state.result?.removed)
        ? state.result.removed.length
        : null
  let completedLabel = 'Population diff complete'
  if (added !== null || removed !== null) {
    const parts: string[] = []
    if (added !== null) parts.push(`+${added}`)
    if (removed !== null) parts.push(`-${removed}`)
    completedLabel = `Diff: ${parts.join(' / ')}`
  }
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Comparing populations...'
      completedLabel={completedLabel}
      loadingDetail={pickString(state.args?.snapshotA, state.args?.snapshotB)}
      completedDetail={pickString(state.args?.snapshotA, state.args?.snapshotB)}
      completedIcon={
        <GitCompare className='h-3.5 w-3.5 shrink-0 text-text-placeholder' />
      }
    />
  )
}

export function PopulationFindEmployeeToolMessage({ part }: ToolPartProps) {
  const state = readToolState(part)
  const records = populationRecords(state.result)
  const matched =
    typeof state.result?.summary?.matched === 'number'
      ? state.result.summary.matched
      : records.length
  const excludedMatches =
    typeof state.result?.summary?.excludedMatches === 'number'
      ? state.result.summary.excludedMatches
      : 0
  const query = pickString(state.args?.query)

  // "No matches" is the wrong headline when the person exists but is marked
  // excluded from compliance — the server distinguishes the two cases, and
  // the status line has to as well or the UI contradicts the answer text.
  const completedLabel =
    matched === 0
      ? excludedMatches > 0
        ? `${plural(excludedMatches, 'excluded account')} matched`
        : 'No match'
      : `${plural(matched, 'person', 'people')} found`

  return (
    <>
      <ToolStatusLine
        state={state}
        loadingLabel='Looking up the person...'
        completedLabel={completedLabel}
        loadingDetail={query}
        completedDetail={query}
        completedIcon={usersIcon}
      />
      {state.isComplete && !isToolError(state) && matched > 0 ? (
        <ToolListPreview
          emptyLabel='No match'
          items={records.map((record) => ({
            id: null,
            title: displayName(record),
            subtitle: pickString(record.email, record.title),
            meta: [
              fieldValue(record, 'title', 'jobTitle'),
              fieldValue(record, 'department'),
              fieldValue(record, 'status'),
            ],
          }))}
        />
      ) : null}
    </>
  )
}

export function PopulationListComplianceRoleHoldersToolMessage({
  part,
}: ToolPartProps) {
  const state = readToolState(part)
  const count = Array.isArray(state.result?.roles)
    ? state.result.roles.length
    : null
  return (
    <ToolStatusLine
      state={state}
      loadingLabel='Loading compliance roles...'
      completedLabel={
        count !== null ? `${plural(count, 'role')} loaded` : 'Roles loaded'
      }
      completedIcon={usersIcon}
    />
  )
}
